import React, { Suspense, lazy } from 'react'
import { Navigate, type RouteObject } from 'react-router-dom'
import Explore from './components/Explore'
import SecurityAndPrivacyPolicy from './components/SecurityAndPrivacyPolicy'
import ContactUs from './components/ContactUs'
import Maintenance from './components/Maintenance'
import Cookies from './components/Cookies'
import TermsOfService from './components/TermsOfService'
import Landing from './components/Landing'
import Register from './components/Register'
import FAQ from './components/FAQ'
import Support from './components/Support'
import Feedback from './components/Feedback'
import Socials from './components/Socials'
import SEOHead from './components/SEOHead'
import { useSEO } from './hooks/useSEO'
import { useFocusManagement, useKeyboardNavigation } from './hooks/useFocusManagement'

function RouteWithSEO({ children }: { children: React.ReactNode }) {
  const seoConfig = useSEO()
  useFocusManagement()
  useKeyboardNavigation()
  return (
    <>
      <SEOHead config={seoConfig} />
      {children}
    </>
  )
}

// Lazy-loaded on the client only — /blog is not statically prerendered, so server
// rendering is unaffected (the route hydrates client-side as before, but the Blog
// component + blogPosts data are a separate chunk instead of loading on first paint).
const Blog = lazy(() => import('./components/Blog'))

export const routes: RouteObject[] = [
  {
    path: '/',
    element: (
      <RouteWithSEO>
        <Landing />
      </RouteWithSEO>
    ),
  },
  { path: '/about-us', element: <Navigate to="/explore" replace /> },
  {
    path: '/explore',
    element: (
      <RouteWithSEO>
        <Explore />
      </RouteWithSEO>
    ),
  },
  {
    path: '/register',
    element: (
      <RouteWithSEO>
        <Register />
      </RouteWithSEO>
    ),
  },
  {
    path: '/sap-policy',
    element: (
      <RouteWithSEO>
        <SecurityAndPrivacyPolicy />
      </RouteWithSEO>
    ),
  },
  {
    path: '/contact-us',
    element: (
      <RouteWithSEO>
        <ContactUs />
      </RouteWithSEO>
    ),
  },
  {
    path: '/maintenance',
    element: (
      <RouteWithSEO>
        <Maintenance />
      </RouteWithSEO>
    ),
  },
  {
    path: '/cookies',
    element: (
      <RouteWithSEO>
        <Cookies />
      </RouteWithSEO>
    ),
  },
  {
    path: '/tos',
    element: (
      <RouteWithSEO>
        <TermsOfService />
      </RouteWithSEO>
    ),
  },
  {
    path: '/faq',
    element: (
      <RouteWithSEO>
        <FAQ />
      </RouteWithSEO>
    ),
  },
  {
    path: '/support',
    element: (
      <RouteWithSEO>
        <Support />
      </RouteWithSEO>
    ),
  },
  {
    path: '/feedback',
    element: (
      <RouteWithSEO>
        <Feedback />
      </RouteWithSEO>
    ),
  },
  {
    path: '/socials',
    element: (
      <RouteWithSEO>
        <Socials />
      </RouteWithSEO>
    ),
  },
  {
    path: '/blog',
    element: (
      <RouteWithSEO>
        <Suspense fallback={<div className="min-h-[60vh] flex items-center justify-center text-text-primary/70">Loading…</div>}>
          <Blog />
        </Suspense>
      </RouteWithSEO>
    ),
  },
  {
    path: '/blog/:slug',
    element: (
      <RouteWithSEO>
        <Suspense fallback={<div className="min-h-[60vh] flex items-center justify-center text-text-primary/70">Loading…</div>}>
          <Blog />
        </Suspense>
      </RouteWithSEO>
    ),
  },
  { path: '*', element: <Navigate to="/" replace /> },
]